// The buttons that lead to a room (COM-FE-05): "Appeler" in a chat, "Démarrer une réunion" in a
// project, "Rejoindre" for a meeting of the calendar.
import { FormField } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { Call20Regular, Video20Regular } from '@fluentui/react-icons';
import { useNavigate } from '@tanstack/react-router';
import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import { useCreateMeeting, useStartCall } from './api';
import { reserveCallWindow, showCallIn } from './call-window';

/**
 * "Appeler": starts a call in the chat and opens it in the call window (COMX-FE-08). The window is
 * opened at once, empty, inside the click (or the browser blocks it), and shown the call when the
 * API has created it. Blocked windows: the call opens in this tab.
 */
export function StartCallButton({ orgId, channelId }: { orgId: string; channelId: string }) {
  const { t } = useTranslation('meetings');
  const navigate = useNavigate();
  const notify = useNotify();
  const start = useStartCall();
  return (
    <Button
      appearance="subtle"
      icon={<Call20Regular />}
      disabledFocusable={start.isPending}
      onClick={() => {
        if (start.isPending) return;
        const target = reserveCallWindow();
        start.mutate(
          { orgId, data: { channelId } },
          {
            onSuccess: (meeting) => {
              if (target && !target.closed) showCallIn(target, meeting.id);
              else void navigate({ to: '/calls/$meetingId', params: { meetingId: meeting.id } });
            },
            onError: () => {
              target?.close();
              notify({ intent: 'error', message: t('call.failed') });
            },
          },
        );
      }}
    >
      {t('call.action')}
    </Button>
  );
}

/** "Rejoindre": opens the room of a meeting (the calendar shows it for events that have one). */
export function JoinMeetingButton({ orgId, meetingId }: { orgId: string; meetingId: string }) {
  const { t } = useTranslation('meetings');
  const navigate = useNavigate();
  return (
    <Button
      appearance="primary"
      icon={<Video20Regular />}
      onClick={() => {
        void navigate({ to: '/o/$orgId/meetings/$meetingId', params: { orgId, meetingId } });
      }}
    >
      {t('join.action')}
    </Button>
  );
}

export interface StartMeetingButtonProps {
  /** The organisation in the address; null in a shared space (the standalone room opens then). */
  orgId: string | null;
  /** The button's text: "Démarrer une réunion" (header) or "Nouvelle réunion" (the tab). */
  label?: 'start' | 'new';
  /** The project's space: the meeting belongs to it, so its members can join. */
  spaceId: string;
  projectName: string;
}

/** "Démarrer une réunion": asks for a title, creates the meeting and opens its room. */
export function StartMeetingButton({
  orgId,
  spaceId,
  projectName,
  label = 'start',
}: StartMeetingButtonProps) {
  const { t } = useTranslation('meetings');
  const navigate = useNavigate();
  const create = useCreateMeeting();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const close = () => {
    setOpen(false);
    setFailed(false);
    setTitleError(null);
  };

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (create.isPending) return;
    const value = title.trim();
    if (value === '' || value.length > 200) {
      setTitleError(t('start.titleInvalid'));
      return;
    }
    setTitleError(null);
    setFailed(false);
    try {
      const meeting = await create.mutateAsync({ spaceId, data: { title: value } });
      close();
      if (orgId !== null) {
        await navigate({
          to: '/o/$orgId/meetings/$meetingId',
          params: { orgId, meetingId: meeting.id },
        });
      } else {
        await navigate({ to: '/meetings/$meetingId', params: { meetingId: meeting.id } });
      }
    } catch {
      setFailed(true);
    }
  };

  return (
    <>
      <Button
        icon={<Video20Regular />}
        onClick={() => {
          setTitle(t('start.defaultTitle', { project: projectName }));
          setOpen(true);
        }}
      >
        {label === 'new' ? t('start.new') : t('start.action')}
      </Button>
      <Dialog
        open={open}
        modalType="modal"
        onOpenChange={(_event, data) => {
          if (!data.open) close();
        }}
      >
        <DialogSurface>
          <form
            noValidate
            onSubmit={(event) => {
              void submit(event);
            }}
          >
            <DialogBody>
              <DialogTitle>{t('start.title')}</DialogTitle>
              <DialogContent>
                <FormField required label={t('start.titleLabel')} error={titleError}>
                  <Input
                    value={title}
                    maxLength={200}
                    onChange={(_event, data) => {
                      setTitle(data.value);
                      setTitleError(null);
                    }}
                  />
                </FormField>
                {failed ? (
                  <MessageBar intent="error" layout="multiline">
                    <MessageBarBody>{t('start.failed')}</MessageBarBody>
                  </MessageBar>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" type="button" onClick={close}>
                  {t('start.cancel')}
                </Button>
                <Button appearance="primary" type="submit" disabledFocusable={create.isPending}>
                  {t('start.confirm')}
                </Button>
              </DialogActions>
            </DialogBody>
          </form>
        </DialogSurface>
      </Dialog>
    </>
  );
}

export interface StartChannelMeetingButtonProps {
  /** The space of the channel: the meeting belongs to it. */
  spaceId: string;
  channelId: string;
  channelName: string;
}

/**
 * "Démarrer une réunion" in a project or space channel: creates a meeting held in the channel
 * (its card appears in the conversation) and opens the standalone room, which also works for a
 * partner of a shared space.
 */
export function StartChannelMeetingButton({
  spaceId,
  channelId,
  channelName,
}: StartChannelMeetingButtonProps) {
  const { t } = useTranslation('meetings');
  const navigate = useNavigate();
  const notify = useNotify();
  const create = useCreateMeeting();
  return (
    <Button
      appearance="subtle"
      icon={<Video20Regular />}
      disabledFocusable={create.isPending}
      onClick={() => {
        if (create.isPending) return;
        create.mutate(
          {
            spaceId,
            data: {
              title: t('start.channelTitle', { channel: channelName }).slice(0, 200),
              channelId,
            },
          },
          {
            onSuccess: (meeting) => {
              void navigate({ to: '/meetings/$meetingId', params: { meetingId: meeting.id } });
            },
            onError: () => {
              notify({ intent: 'error', message: t('start.failed') });
            },
          },
        );
      }}
    >
      {t('start.action')}
    </Button>
  );
}
